/**
 * PageHeader — header standard di una pagina dentro AppShell.
 *
 * Slot:
 * - title  obbligatorio (h1)
 * - subtitle opzionale (sotto il titolo)
 * - eyebrow opzionale (label-sm uppercase sopra il titolo, es. "Lunedì 12 maggio")
 * - actions opzionale (CTA destre, su mobile vanno a capo sotto)
 *
 * Responsive: titolo scala da text-2xl (mobile) a headline-xl (desktop)
 * per rimanere leggibile a 320px (vedi rule responsive).
 */

import type { ReactNode } from 'react';
import { clsx } from 'clsx';

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <header
      className={clsx(
        'flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-6',
        'mb-6 md:mb-8',
        className
      )}
    >
      <div className="min-w-0">
        {eyebrow && (
          <p className="font-label-sm text-label-sm uppercase tracking-wide text-text-secondary mb-2">
            {eyebrow}
          </p>
        )}
        <h1 className="font-headline-xl text-2xl md:text-headline-xl text-text-main leading-tight break-words">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-2 font-body-md text-text-secondary">{subtitle}</p>
        )}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2 md:gap-3 md:flex-shrink-0">
          {actions}
        </div>
      )}
    </header>
  );
}
